import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CancellationsApi from '../../../api/CancellationsApi';
import PolicyApi from '../../../api/PolicyApi';
import { AuthData } from '../../../context/auth';
import {
	EndorsementData,
	withEndorsementContext,
} from '../../../context/EndorsementProvider';
import { Exposure, ExposureCreateRequest } from '../../../types/policy';
import {
	StyledSectionContainer,
	StyledTitle,
	StyledTitleContainer,
} from '../../common';
import { errorToast, successToast } from '../../Toast';
import ConfirmationModal from '../ConfirmationModal';
import AddExposureModal from './Exposures/AddExposureModal';
import ExposuresList from './Exposures/ExposuresList';
import { getExposuresForEndorsement } from './util';

interface Props {
	authData: AuthData;
	endorsementData: EndorsementData;
}

/**
 * The exposure list on an endorsement.
 */
const Exposures = (props: Props) => {
	const { t } = useTranslation();
	const { getValues, formState, reset } = useFormContext();
	const { dirtyFields } = formState;

	const [isConfirmModalOpen, setIsConfirmModalOpen] = React.useState(false);
	const [removedExposure, setRemoveExposure] = React.useState<
		Exposure | ExposureCreateRequest
	>(null);
	const { authData, endorsementData } = props;

	const {
		endorsementUpdateObj,
		// setEndorsementUpdateObj,
		productConfig,
		policy,
		setLoading,
		saveFastForm,
	} = endorsementData;

	const { exposures } = policy;

	const [graceLapseReinstatementData, setGraceLapse] = useState([]);
	const [cancellations, setCancellations] = useState([]);

	useEffect(() => {
		const fetchCancellationsAndGraceLapse = async () => {
			setLoading(true);
			try {
				const cancellationResponse =
					await CancellationsApi.getCancellationsForPolicy(
						authData.auth.authorizationToken,
						policy.locator,
					);
				if (cancellationResponse) {
					setCancellations(cancellationResponse.data);
				}
				const graceLapse = await PolicyApi.getGraceLapseAndReinstatementInfo(
					authData.auth.authorizationToken,
					policy.locator,
				);
				if (graceLapse) {
					setGraceLapse(graceLapse.data);
				}
			} catch (err) {
				console.error(err);
				setLoading(false);
			}

			setLoading(false);
		};
		fetchCancellationsAndGraceLapse();
	}, []);

	// only show exposures that were active/present prior to the effective date of the endorsement
	const endorsementEffectiveDate = endorsementUpdateObj.startTimestamp;
	const activeExposuresAtTimeOfEndorsement = getExposuresForEndorsement(
		endorsementEffectiveDate,
		policy,
		exposures,
		cancellations,
		graceLapseReinstatementData,
	);

	const policyConfig = productConfig.policyConfiguration;

	const removeExposure = async () => {
		try {
			setLoading(true);
			setIsConfirmModalOpen(false);
			await saveFastForm(
				getValues(),
				dirtyFields,
				reset,
				// displayId only exists when it's an exposure from existing policy
				{
					['displayId' in removedExposure
						? 'removeExposure'
						: 'removeAddedExposure']: {
						exposureLocator: removedExposure.locator,
					},
				},
			);
			successToast('Successfully removed exposure');
		} catch (e) {
			errorToast('Error removing exposure');
		} finally {
			setLoading(false);
		}
	};

	const renderActions = () => {
		return (
			<AddExposureModal
				policyConfig={policyConfig}
				authData={authData}
				endorsementData={endorsementData}
			/>
		);
	};

	const renderRemoveExposureModal = () => {
		return (
			<ConfirmationModal
				entityName={
					(removedExposure as Exposure).name ||
					(removedExposure as ExposureCreateRequest).exposureName
				}
				entityType={t('exposure.singular.lower')}
				authToken={authData.auth.authorizationToken}
				onRemove={() => removeExposure()}
				cancel={() => setIsConfirmModalOpen(false)}
			/>
		);
	};

	const openConfirmationModal = (exposure: Exposure) => {
		if (exposure) {
			setIsConfirmModalOpen(true);
			setRemoveExposure(exposure);
		}
	};

	const hasWritePermissions =
		authData.hasPermission('endorsement.update') &&
		endorsementUpdateObj.state === 'application';
	return (
		<>
			<StyledTitleContainer>
				<StyledTitle>{t('exposure.plural.upper')}</StyledTitle>
				{isConfirmModalOpen && renderRemoveExposureModal()}
			</StyledTitleContainer>
			<hr />
			<StyledSectionContainer>
				<ExposuresList
					exposures={activeExposuresAtTimeOfEndorsement}
					openConfirmationModal={openConfirmationModal}
				/>
			</StyledSectionContainer>
			{hasWritePermissions && renderActions()}
		</>
	);
};

export default withEndorsementContext<Props>(Exposures);
